import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import {
    faBan,
    faCheck,
    faEnvelope,
    faEyes,
    faGhost,
    faTrash,
    faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAuctionBidder, requestBidderPaymentMethod, updateAuctionBidder } from 'api/campaigns';
import { CopyButton } from 'components/copy-button';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as DropdownMenu from 'components/dropdown-menu';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import {
    AddressReadable,
    copyAddress,
} from 'components/pages/campaign-settings/campaign-auction/AddressReadable';
import * as AnonymizeBidderDialog from 'components/pages/campaign-settings/campaign-auction/auction-bidders/AnonymizeBidderDialog';
import * as BanBidderDialog from 'components/pages/campaign-settings/campaign-auction/auction-bidders/BanBidderDialog';
import * as BidderForm from 'components/pages/campaign-settings/campaign-auction/auction-bidders/BidderForm';
import * as DeleteBidderDialog from 'components/pages/campaign-settings/campaign-auction/auction-bidders/DeleteBidderDialog';
import * as Tooltip from 'components/tooltip';
import { TruncatedText } from 'components/truncated-text';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { isoDateFromServer } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Address } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface EditBidderFormValues {
    manualBidderNumber: number;
}

const EditBidderDrawer = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { goBack } = useDialogRoute();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const { data: auction } = useCurrentAuction();
    const campaignId = campaign.id;
    const auctionId = auction.id;
    const { bidderId } = useParams<{ bidderId: string }>();
    const auctionRequiresPaymentMethod = auction.require_payment_method;

    const { data: bidder } = useSuspenseQuery({
        queryKey: campaignQueryKeys.auctionBidderById(campaignId, auctionId, parseInt(bidderId)),
        queryFn: () => fetchAuctionBidder(campaignId, auctionId, parseInt(bidderId)),
    });

    const methods = useGivebutterForm<EditBidderFormValues>({
        defaultValues: {
            manualBidderNumber: bidder.number,
        },
    });

    const { resetFormButton, handleSubmit } = methods;

    const { mutate: submit, isPending: isSubmitting } = useMutation({
        mutationFn: (formValues: EditBidderFormValues) =>
            updateAuctionBidder(campaignId, auctionId, parseInt(bidderId), {
                number: unmaskNumber(formValues.manualBidderNumber),
            }),

        onSuccess: () => {
            toast.success('Bidder successfully updated!');

            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionBidders(campaignId, auctionId),
            });

            goBack();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data?.message ?? `Unable to update bidder.`);
            resetFormButton();
        },
    });

    const { mutate: requestPayment, isPending: isRequesting } = useMutation({
        mutationFn: () => requestBidderPaymentMethod(campaignId, auctionId, parseInt(bidderId)),

        onSuccess: () => {
            toast.success('Bidder payment method requested!');

            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionBidderById(
                    campaignId,
                    auctionId,
                    parseInt(bidderId),
                ),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                error.response?.data?.message ?? `Unable to request bidder payment method.`,
            );
            resetFormButton();
        },
    });

    const completeRegistrationText = `A notification will be sent to the email on file requesting
        the bidder to complete their registration${
            auctionRequiresPaymentMethod ? ' and add a payment method.' : '.'
        }`;

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<EditBidderFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Manage bidder</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body className="flex flex-col gap-6">
                    <div className={'flex flex-row justify-between gap-4'}>
                        <div className="flex flex-col gap-4">
                            <div>
                                <Label>Bidder</Label>
                                <div className={'flex items-start gap-4'}>
                                    <div className={'flex flex-col gap-1'}>
                                        <p className={'text-2xl font-medium'}>
                                            {bidder.first_name} {bidder.last_name}
                                        </p>
                                        <div className={'flex max-w-60 items-center text-gray-600'}>
                                            <TruncatedText>
                                                <p>{bidder.email}</p>
                                            </TruncatedText>
                                            <CopyButton text={bidder.email ?? ''} />
                                        </div>
                                        {bidder.phone && (
                                            <div className={'flex items-center text-gray-600'}>
                                                <p>{getFormattedPhoneNumber(bidder.phone)}</p>
                                                <CopyButton text={bidder.phone} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={'flex text-gray-600'}>
                                {bidder.address && (
                                    <>
                                        <AddressReadable address={bidder.address as Address} />
                                        <CopyButton text={copyAddress(bidder.address as Address)} />
                                    </>
                                )}
                            </div>
                        </div>
                        <div className={'flex flex-row gap-1'}>
                            <AnonymizeBidderDialog.Root bidder={bidder}>
                                <AnonymizeBidderDialog.Trigger className="flex">
                                    <Button size={'sm'} variant={'outline'} color={'neutral'}>
                                        <FontAwesomeIcon
                                            icon={bidder.anonymous ? faEyes : faGhost}
                                        />
                                        <span className="font-medium">
                                            {bidder.anonymous ? 'Make public' : 'Make anonymous'}
                                        </span>
                                    </Button>
                                </AnonymizeBidderDialog.Trigger>
                            </AnonymizeBidderDialog.Root>
                            <DropdownMenu.Root>
                                <DropdownMenu.EllipsisTrigger className="rounded-md border border-gray-300 bg-white pt-[15px]" />
                                <DropdownMenu.Content sideOffset={5} align={'end'}>
                                    <DropdownMenu.LinkItem
                                        to={`/accounts/${accountId}/contacts/${bidder.contact_id}`}
                                    >
                                        <div className={'flex items-center gap-x-2'}>
                                            <FontAwesomeIcon icon={faUser} />
                                            View contact
                                        </div>
                                    </DropdownMenu.LinkItem>
                                    <BanBidderDialog.Root bidder={bidder}>
                                        <BanBidderDialog.Trigger asChild>
                                            <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                                <div className={'flex items-center gap-x-2.5'}>
                                                    <FontAwesomeIcon
                                                        className={'flex h-3 w-3'}
                                                        icon={
                                                            bidder.status === 'banned'
                                                                ? faCheck
                                                                : faBan
                                                        }
                                                    />
                                                    {bidder.status === 'banned' ? 'Unban' : 'Ban'}
                                                </div>
                                            </DropdownMenu.Item>
                                        </BanBidderDialog.Trigger>
                                    </BanBidderDialog.Root>
                                    {bidder.cant_delete ? (
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <DropdownMenu.Item
                                                    disabled
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    <div className={'flex items-center gap-x-2.5'}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                        Delete bidder
                                                    </div>
                                                </DropdownMenu.Item>
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content inverse>
                                                    <Tooltip.Arrow inverse />
                                                    <p>
                                                        This bidder cannot be deleted because they
                                                        have associated auction bids.
                                                    </p>
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    ) : (
                                        <DeleteBidderDialog.Root bidder={bidder}>
                                            <DeleteBidderDialog.Trigger asChild>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                    className="flex items-center gap-2"
                                                >
                                                    <div className={'flex items-center gap-x-2.5'}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                        Delete bidder
                                                    </div>
                                                </DropdownMenu.Item>
                                            </DeleteBidderDialog.Trigger>
                                        </DeleteBidderDialog.Root>
                                    )}
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    </div>
                    <Separator className={`-mx-8 !w-auto`} />
                    <div className="flex flex-col gap-12">
                        {!bidder.verified ? (
                            <div>
                                <Label>
                                    {auctionRequiresPaymentMethod
                                        ? 'Request bidder to add payment method'
                                        : 'Request bidder to complete registration'}
                                </Label>
                                <div className={'flex flex-col items-start gap-4'}>
                                    <div className={'flex flex-col gap-1'}>
                                        <p className={'text-sm text-gray-600'}>
                                            {completeRegistrationText}
                                        </p>
                                    </div>
                                    <div className={'flex items-center gap-2 text-xs'}>
                                        <Button
                                            size={'sm'}
                                            variant={'outline'}
                                            color={'neutral'}
                                            onClick={() => requestPayment()}
                                            disabled={isRequesting}
                                        >
                                            <FontAwesomeIcon icon={faEnvelope} />
                                            <span className="font-medium">Send request</span>
                                        </Button>
                                        {bidder.payment_method_requested_at && (
                                            <p className={'text-gray-700'}>
                                                Last request sent{' '}
                                                <span>
                                                    {isoDateFromServer(
                                                        bidder.payment_method_requested_at,
                                                    ).toLocaleString(DateTime.DATE_FULL)}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex w-72 justify-between">
                                <div className="flex flex-col gap-1">
                                    <Label>Date added</Label>
                                    <p className="text-gray-600">
                                        {isoDateFromServer(bidder.created_at).toLocaleString(
                                            DateTime.DATE_FULL,
                                        )}
                                    </p>
                                </div>
                                {bidder.verified_at && (
                                    <div className="flex flex-col gap-1">
                                        <Label>Date registered</Label>
                                        <p className="text-gray-600">
                                            {isoDateFromServer(bidder.verified_at).toLocaleString(
                                                DateTime.DATE_FULL,
                                            )}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        <EditableInput.Root>
                            <BidderForm.Root bidder={bidder}>
                                <Label>Bidder ID #</Label>
                                <EditableInput.ReadOnly>
                                    <BidderForm.StartingNumberReadOnly />
                                    <EditableInput.ReadOnlyEditTrigger />
                                </EditableInput.ReadOnly>
                                <EditableInput.Content>
                                    <BidderForm.StartingNumber />
                                    <EditableInput.ActionsContainer>
                                        <EditableInput.Cancel />
                                    </EditableInput.ActionsContainer>
                                </EditableInput.Content>
                            </BidderForm.Root>
                        </EditableInput.Root>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <Form.SubmitButton isSubmitting={isSubmitting}>
                            Save Edits
                        </Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { EditBidderDrawer };
export type { EditBidderFormValues };
